import { getCurrentCustomerDetails, getCurrentCustomerId } from 'src/app/customers/state/customersSelector';
import { useAppDispatch, useAppSelector } from 'src/store';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useProjectsQuery } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import useAuthService from 'src/shared/hooks/useAuthService';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { resetPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSlice';
import { clearAllGnrState } from 'src/app/portfolio/views/global-nature-risk/state/gnrFiltersSlice';
import { setCurrentProjectId } from 'src/app/projects/views/project-list/state/projectListSlice';
import { useLazyGetOrganisationQuery } from 'src/app/admin/views/risk-portfolio/state/api/organisationEngineSlice';
import { setCurrentCustomerId as setCurrentCustomerIdAction } from '../state/customersSlice';
import { useCustomersQuery } from '../state/api/customersGraphSlice';

const useCustomer = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { pathname } = useLocation();
    const customerIdFromUrl = searchParams.get('customerId');

    const currentCustomerId = useAppSelector(getCurrentCustomerId) || customerIdFromUrl || '';
    const currentCustomerDetails = useAppSelector(getCurrentCustomerDetails);
    const { currentData: allCustomers } = useCustomersQuery({});

    const currentCustomerName = allCustomers?.customers.find(customer => customer.customerId === currentCustomerId)?.customerName || '';
    const currentCustomerHasPortfolio =
        allCustomers?.customers.find(customer => customer.customerId === currentCustomerId)?.hasPortfolio || false;
    const dispatch = useAppDispatch();
    const { isAuthenticated } = useAuthService();
    const navigate = useAppNavigation();
    const [triggerGetOrganisation] = useLazyGetOrganisationQuery();

    const {
        isFetching: isFetchingCurrentCustomerProjects,
        currentData: currentCustomerProjects,
        error,
    } = useProjectsQuery(
        {
            customerId: currentCustomerId || '',
        },
        {
            skip: !currentCustomerId || !isAuthenticated,
            refetchOnFocus: true,
            refetchOnReconnect: true,
        }
    );

    useEffect(() => {
        if (customerIdFromUrl && customerIdFromUrl !== currentCustomerId) {
            setCurrentCustomerId(customerIdFromUrl);
        } else if (!currentCustomerId && allCustomers?.customers && allCustomers.customers.length > 0 && isAuthenticated) {
            // Automatically set the first customer if none is selected, excluding Extractives Customer (Unless Extractives Customer is the only customer)
            const nonDemoCustomers = allCustomers.customers.filter(customer => customer.customerId !== 'Extractives Customer');
            setCurrentCustomerId(nonDemoCustomers[0]?.customerId || allCustomers.customers[0]?.customerId);
        }
    }, [customerIdFromUrl, currentCustomerId, allCustomers, isAuthenticated]);

    // Don't throw for transient network/api errors – allow UI to continue and auto-refetch on focus/reconnect
    if (error) {
        console.warn('Projects list fetch error (non-fatal):', error);
    }

    const setCurrentCustomerId = (customerId: string) => {
        dispatch(setCurrentCustomerIdAction(customerId));
        // Update URL with new customer ID. Drop any projectId param too — it belongs to the previous
        // customer, and leaving it in the URL would let useProject()'s URL-sync effect resurrect it
        // into currentProjectId before navigation away from a project-scoped page takes effect.
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('customerId', customerId);
        newSearchParams.delete('projectId');
        setSearchParams(newSearchParams);
    };

    const currentCustomerProjectIds = currentCustomerProjects?.map(project => project.projectId) || [];

    // Explicit user-initiated org switch: resets customer-scoped filters and the selected project
    // (currentProjectId persists in storage, so leaving it set would keep the Nature Performance
    // sidebar items pointing at a project belonging to the old org), then lands on Portfolio Summary
    // when the org has a portfolio, Global Nature Risk Overview when it's engine-onboarded but has no
    // portfolio, or stays put otherwise — unless staying put would leave the user on a site-scoped
    // Nature Risk page for a site that no longer belongs to the new org, or on a portfolio-scoped page
    // for an org with no portfolio, in which case they're sent to My Sites (both the sidebar's Nature
    // Risk section and the portfolio pages are derived purely from the URL, so they can't self-correct).
    const switchCustomer = async (customerId: string) => {
        if (customerId === currentCustomerId) {
            return;
        }

        const wasOnSiteNatureRiskPage = pathname.startsWith('/site-nature-risk/');
        const wasOnPortfolioPage = pathname.startsWith('/portfolio');

        dispatch(resetPortfolioFilters());
        dispatch(clearAllGnrState());
        dispatch(setCurrentProjectId(null));
        setCurrentCustomerId(customerId);

        const targetCustomer = allCustomers?.customers.find(customer => customer.customerId === customerId);
        if (targetCustomer?.hasPortfolio) {
            navigate.toPortfolioSummary(customerId);
            return;
        }

        const { data: organisation } = await triggerGetOrganisation(customerId);
        if (organisation) {
            navigate.toGlobalNatureRiskOverview();
            return;
        }

        if (wasOnSiteNatureRiskPage || wasOnPortfolioPage) {
            navigate.toProjects({ customerId });
        }
    };

    return {
        allCustomers,
        currentCustomerId,
        currentCustomerDetails,
        currentCustomerName,
        currentCustomerHasPortfolio,
        setCurrentCustomerId,
        switchCustomer,
        currentCustomerProjects,
        isFetchingCurrentCustomerProjects,
        currentCustomerProjectIds,
        error: error as unknown as Error,
    };
};

export default useCustomer;
